import { gql } from '@apollo/client';

export const EXECUTE_REFUND = gql`
  mutation ExecuteRefund($input: ExecuteRefundInput!) {
    executeRefund(input: $input) {
      id # Stripe refund ID
      amount # 退款金额
      status # Stripe refund status
      currency # 货币类型 (USD)
      receiptNumber # 收据编号
    }
  }
`;

// ========== DEPOSIT-FIRST FLOW MUTATIONS ==========

export const CREATE_SETUP_INTENT_FOR_BOOKING = gql`
  mutation CreateSetupIntentForBooking($bookingId: String!, $showSavedCards: Boolean) {
    createSetupIntentForBooking(bookingId: $bookingId, showSavedCards: $showSavedCards) {
      clientSecret
      setupIntentId
      customerSessionClientSecret
      customerId
    }
  }
`;

export const CREATE_DEPOSIT_PAYMENT_INTENT = gql`
  mutation CreateDepositPaymentIntent($input: CreateDepositPaymentIntentInput!) {
    createDepositPaymentIntent(input: $input) {
      clientSecret
      paymentIntentId
      status
      requiresAction
    }
  }
`;

export const CREATE_RENTAL_PAYMENT_INTENT = gql`
  mutation CreateRentalPaymentIntent($input: CreateRentalPaymentIntentInput!) {
    createRentalPaymentIntent(input: $input) {
      clientSecret
      paymentIntentId
      status
      requiresAction
      booking {
        id
        status
      }
    }
  }
`;

// ========== SPEC 013: SERVER-PRICED TRIP EXTENSION ==========

/**
 * The server prices the extension, charges the chosen (or preferred) card on-session and
 * reports the outcome. The trip is extended by `confirmTripExtension(paymentIntentId)`
 * once the intent succeeded; a REQUIRES_ACTION result carries the client secret for 3DS.
 */
export const CREATE_TRIP_EXTENSION_PAYMENT = gql`
  mutation CreateTripExtensionPayment($input: CreateTripExtensionPaymentInput!) {
    createTripExtensionPayment(input: $input) {
      attemptId
      status
      paymentIntentId
      clientSecret
      amount
      errorCode
      declineCode
      errorMessage
      extensionBlocked
    }
  }
`;

// ========== SPEC 013 P3: PICK ANOTHER CARD FOR AN EXTENSION ==========

/**
 * Cards the guest can pay this booking's extension with: the booking's own cards first
 * (`usedByThisBooking`), then the guest's saved cards on the booking's brand.
 */
export const BOOKING_PAYMENT_OPTIONS = gql`
  query BookingPaymentOptions($bookingId: ID!) {
    bookingPaymentOptions(bookingId: $bookingId) {
      preferredPaymentMethodId
      extensionBlocked
      consecutiveExtensionDeclines
      cards {
        id
        brand
        last4
        expMonth
        expYear
        wallet
        isExpired
        isDefault
        usedByThisBooking
      }
    }
  }
`;

// ========== SPEC 013 P4: MANAGE SAVED CARDS ==========

/** The guest's saved cards on this site (request brand). */
export const MY_SAVED_PAYMENT_METHODS = gql`
  query MySavedPaymentMethods {
    mySavedPaymentMethods {
      id
      brand
      last4
      billingLine1
      billingLine2
      billingCity
      billingState
      billingPostalCode
      expMonth
      expYear
      wallet
      isExpired
      isDefault
      allowRedisplay
      inUseByBookingIds
    }
  }
`;

export const CREATE_SETUP_INTENT_FOR_SAVED_CARD = gql`
  mutation CreateSetupIntentForSavedCard {
    createSetupIntentForSavedCard {
      clientSecret
      setupIntentId
      customerId
      publishableKey
    }
  }
`;

/** Name check, duplicate check and "save" after the card form confirmed the SetupIntent. */
export const CONFIRM_SAVED_CARD = gql`
  mutation ConfirmSavedCard($setupIntentId: String!) {
    confirmSavedCard(setupIntentId: $setupIntentId) {
      duplicate
      card {
        id
        brand
        last4
        expMonth
        expYear
        wallet
        isExpired
        isDefault
        allowRedisplay
        inUseByBookingIds
      }
    }
  }
`;

export const SET_DEFAULT_PAYMENT_METHOD = gql`
  mutation SetDefaultPaymentMethod($paymentMethodId: ID!) {
    setDefaultPaymentMethod(paymentMethodId: $paymentMethodId) {
      id
      isDefault
    }
  }
`;

/** Removes the card, or only hides it while a trip may still charge it (FR-018). */
export const REMOVE_PAYMENT_METHOD = gql`
  mutation RemovePaymentMethod($paymentMethodId: ID!) {
    removePaymentMethod(paymentMethodId: $paymentMethodId) {
      removed
      hidden
      inUseByBookingIds
    }
  }
`;

// ========== SPEC 013 P5: PAY UNPAID POST-TRIP CHARGES ==========

/** Unpaid post-trip charges of a trip the guest can pay in the app. */
export const OUTSTANDING_POST_TRIP_INVOICES = gql`
  query OutstandingPostTripInvoices($id: String!) {
    getBookingAsGuest(id: $id) {
      id
      outstandingPostTripInvoices {
        id
        type
        amount
        status
        description
        stripeInvoiceUrl
        createdAt
      }
    }
  }
`;

export const PAY_OUTSTANDING_INVOICE = gql`
  mutation PayOutstandingInvoice($input: PayOutstandingInvoiceInput!) {
    payOutstandingInvoice(input: $input) {
      attemptId
      status
      paymentIntentId
      clientSecret
      amount
      errorCode
      declineCode
      errorMessage
    }
  }
`;

/** After the bank check: the server verifies the intent with Stripe and settles the charge. */
export const CONFIRM_OUTSTANDING_INVOICE_PAYMENT = gql`
  mutation ConfirmOutstandingInvoicePayment($invoiceId: ID!, $paymentIntentId: String!) {
    confirmOutstandingInvoicePayment(invoiceId: $invoiceId, paymentIntentId: $paymentIntentId) {
      status
      paymentIntentId
    }
  }
`;
